/**
 * Helpers for safely substituting an edited Nebula artifact back into the
 * original full answer string (which may contain multiple fenced blocks).
 *
 * Why this exists: `parseNebulaResponse` strips the fence and trims the
 * content, so by the time NebulaCanvas has the editable string it no longer
 * knows which language tag (`html` / `html-viz` / `html-live`, `react-viz` /
 * `jsx-live`, `physics-viz` / `json-viz`, `mermaid`) the answer originally
 * used. To save we must re-insert the edit in place — without overwriting
 * any other fenced block that happens to appear earlier in the answer.
 *
 * Strategy: scan the raw content for the *first* fence whose tag is in the
 * alias list for this artifact type, capture both the langTag and the body,
 * then delegate to `replaceCodeBlockContent` (which targets a specific
 * block by tag + index) for the actual substitution.
 */

import { getCodeBlockIndex, replaceCodeBlockContent } from "./codeBlockUtils";

// Artifact type → list of fence aliases (matches nebulaParser.js).
const FENCE_ALIASES = {
  html:    ["html-viz", "html-live", "html"],
  react:   ["react-viz", "jsx-live"],
  plotly:  ["physics-viz", "json-viz"],
  mermaid: ["mermaid"],
  markmap: ["markmap"],
};

/**
 * Find the Nth fence (0-based `occurrence`) belonging to `artifactType` in `rawContent`.
 * @returns {{ langTag: string, originalBody: string } | null}
 */
export function findArtifactFence(rawContent, artifactType, occurrence = 0) {
  const aliases = FENCE_ALIASES[artifactType];
  if (!rawContent || !aliases) return null;
  // Build an alternation for this type's aliases, capturing which tag matched.
  const re = new RegExp(
    "```(" + aliases.map(escapeRe).join("|") + ")\\s*\\n([\\s\\S]*?)```",
    "gi"
  );
  let m;
  let idx = 0;
  while ((m = re.exec(rawContent)) !== null) {
    if (idx === occurrence) {
      // Strip optional trailing newline so it lines up with how the body sits in
      // the source between fences (matches `getCodeBlockIndex`'s normalisation).
      const body = m[2].replace(/\n$/, "");
      return { langTag: m[1], originalBody: body };
    }
    idx++;
  }
  return null;
}

/**
 * Build the updated full answer string with `editedCode` substituted for the
 * Nth fence of `artifactType` (default 0). Returns null if no matching fence is found.
 */
export function buildUpdatedNebulaContent(rawContent, artifactType, editedCode, occurrence = 0) {
  const found = findArtifactFence(rawContent, artifactType, occurrence);
  if (!found) return null;
  const blockIndex = getCodeBlockIndex(rawContent, found.langTag, found.originalBody);
  return replaceCodeBlockContent(rawContent, found.langTag, blockIndex, editedCode);
}

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
